import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getAllReadings, getAllOutlets, getUtilities, getStatistics, exportAdminReadingsCSV } from '../api';

function AdminDashboard() {
  const [readings, setReadings] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [utilities, setUtilities] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    utilityId: '',
    outletId: ''
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [readingsRes, outletsRes, utilitiesRes, statsRes] = await Promise.all([
        getAllReadings(filters),
        getAllOutlets(),
        getUtilities(),
        getStatistics()
      ]);
      setReadings(readingsRes.data);
      setOutlets(outletsRes.data);
      setUtilities(utilitiesRes.data);
      setStatistics(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      const response = await getAllReadings(filters);
      setReadings(response.data);
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      utilityId: '',
      outletId: ''
    });
    fetchData();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDownloadCSV = async () => {
    setLoading(true);
    try {
      const res = await exportAdminReadingsCSV(filters);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `readings-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setLoading(false);
    }
  };


  const filteredOutlets = filters.utilityId
    ? outlets.filter(o => o.utilityId === parseInt(filters.utilityId))
    : outlets;

  if (loading) {
    return <div className="loading">Loading dashboard data...</div>;
  }

  return (
    <div>
      <div className="navbar">
        <h1>MeterBill Pro - Admin</h1>
        <div className="navbar-right">
          <button onClick={() => navigate('/admin/users')} className="btn" style={{ marginRight: 8 }}>Manage Users</button>
          <span className="navbar-user">👤 {user?.username}</span>
          <button onClick={handleLogout}>Logout</button>
        </div>
      </div>
      <div className="container">
        <div className="page-header">
          <h2>Admin Dashboard</h2>
          <p>Monitor all meter readings and outlets</p>
        </div>

        {statistics && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Outlets</h3>
              <div className="value">{statistics.totalOutlets}</div>
            </div>
            <div className="stat-card">
              <h3>Total Readings</h3>
              <div className="value">{statistics.totalReadings}</div>
            </div>
            <div className="stat-card">
              <h3>Active Users</h3>
              <div className="value">{statistics.totalUsers}</div>
            </div>
          </div>
        )}

        <div className="filter-section">
          <h3>🔍 Filter Readings</h3>
          <div className="filter-group">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group">
              <label>Utility</label>
              <select
                name="utilityId"
                value={filters.utilityId}
                onChange={handleFilterChange}
              >
                <option value="">All Utilities</option>
                {utilities.map((utility) => (
                  <option key={utility.id} value={utility.id}>
                    {utility.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Outlet</label>
              <select
                name="outletId"
                value={filters.outletId}
                onChange={handleFilterChange}
              >
                <option value="">All Outlets</option>
                {filteredOutlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.tenantName} - {outlet.location}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleApplyFilters} className="btn btn-primary">
              Apply Filters
            </button>
            <button onClick={handleResetFilters} className="btn btn-secondary">
              Reset
            </button>
            <button onClick={handleDownloadCSV} className="btn btn-outline">
              Download CSV
            </button>
          </div>
        </div>

        {/* Create user moved to /admin/users for a cleaner UI */}

        <div className="card">
          <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>📊 All Readings</h3>
          {readings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📭</div>
              <h3>No readings found</h3>
              <p>Try adjusting your filters or wait for users to submit readings</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Utility</th>
                    <th>Tenant</th>
                    <th>Location</th>
                    <th>Image</th>
                    <th>Serial No</th>
                    <th>Reading</th>
                    <th>Submitted By</th>
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.id}>
                      <td style={{ fontSize: '13px' }}>
                        {new Date(reading.createdAt).toLocaleDateString()}<br/>
                        <small style={{ color: '#6b7280' }}>{new Date(reading.createdAt).toLocaleTimeString()}</small>
                      </td>
                      <td><span className="badge badge-primary">{reading.Outlet?.Utility?.name}</span></td>
                      <td><strong>{reading.Outlet?.tenantName}</strong></td>
                      <td>{reading.Outlet?.location}</td>
                      <td>
                        {reading.imagePath ? (
                          (() => {
                            const imageUrl = reading.imagePath.startsWith('http') ? reading.imagePath : `/${reading.imagePath}`;
                            return (
                              <img
                                src={imageUrl}
                                alt="meter"
                                style={{ width: 80, height: 60, objectFit: 'cover', cursor: 'pointer', borderRadius: 4 }}
                                onClick={() => window.open(imageUrl, '_blank')}
                              />
                            );
                          })()
                        ) : (
                          '-'
                        )}
                      </td>
                      <td><span className="badge badge-success">{reading.Outlet?.serialNo}</span></td>
                      <td><strong style={{ fontSize: '16px' }}>{reading.readingValue}</strong></td>
                      <td>{reading.User?.username}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: '700' }}>🏢 All Outlets</h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Utility</th>
                  <th>Tenant Name</th>
                  <th>Location</th>
                  <th>Meter No</th>
                  <th>Serial No</th>
                </tr>
              </thead>
              <tbody>
                {outlets.map((outlet) => (
                  <tr key={outlet.id}>
                    <td><span className="badge badge-primary">{outlet.Utility?.name}</span></td>
                    <td><strong>{outlet.tenantName}</strong></td>
                    <td>{outlet.location}</td>
                    <td>{outlet.meterNo || '-'}</td>
                    <td><span className="badge badge-success">{outlet.serialNo}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
